
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";
import { useProgressInitializer } from "./useProgressInitializer";
import { useResourceShareUpdater } from "./useResourceShareUpdater";

export function useFetchResourceProgress(id: string, isTrainee: boolean) {
  const { session } = useSession();
  const [progress, setProgress] = useState<number | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [isCalculatingProgress, setIsCalculatingProgress] = useState(false);
  
  // Import the hooks we need
  const { initializeProgressRecords } = useProgressInitializer(id);
  const { updateResourceShareWithProgress } = useResourceShareUpdater(id);
  
  const fetchCompletionDate = async () => {
    try {
      if (!session?.user?.id) return;
      
      const { data: shareData, error: shareError } = await supabase
        .from('resource_shares')
        .select('completed_at')
        .eq('resource_id', id)
        .eq('trainee_id', session.user.id)
        .maybeSingle();
        
      if (shareError) {
        console.error('Error fetching completion date:', shareError);
        return;
      }
      
      if (shareData?.completed_at) {
        setCompletedAt(shareData.completed_at);
      } else {
        // If no completion date in resource_shares, try to set one
        const now = new Date().toISOString();
        
        const { error: updateError } = await supabase
          .from('resource_shares')
          .update({ completed_at: now })
          .eq('resource_id', id)
          .eq('trainee_id', session.user.id);
          
        if (!updateError) {
          setCompletedAt(now);
        }
      }
    } catch (error) {
      console.error('Error fetching completion date:', error);
    }
  };
  
  const calculateProgressFromTrainingData = async (): Promise<number | null> => {
    try {
      if (!session?.user?.id) return null;
      
      const { data: filesData, error: filesError } = await supabase
        .from('resource_files')
        .select('id, file_type')
        .eq('resource_id', id);
        
      if (filesError) {
        console.error('Error fetching resource files:', filesError);
        return null;
      }
      
      if (!filesData || filesData.length === 0) {
        console.log('No files found for this resource');
        return 0;
      }
      
      // Wait for training_progress records to be created
      await initializeProgressRecords(filesData);
      
      // Get file progress data
      const { data: progressData, error: progressError } = await supabase
        .from('training_progress')
        .select('file_id, completed')
        .eq('resource_id', id)
        .eq('user_id', session.user.id);
        
      if (progressError) {
        console.error('Error fetching training progress:', progressError);
        return null;
      }
      
      console.log('File progress data:', progressData);
      
      if (!progressData || progressData.length === 0) {
        console.log('No progress records found');
        return 0;
      }
      
      const completedCount = progressData.filter(item => item.completed).length;
      const totalFiles = filesData.length;
      
      const calculatedProgress = totalFiles > 0 
        ? Math.round((completedCount / totalFiles) * 100) 
        : 0;
      
      console.log('Resource progress calculation:', {
        resourceId: id,
        totalFiles,
        completedCount,
        progressData,
        calculatedProgress
      });
      
      await updateResourceShareWithProgress(calculatedProgress);
      
      return calculatedProgress;
    } catch (error) {
      console.error('Error in calculateProgressFromTrainingData:', error);
      return null;
    }
  };
  
  const fetchProgress = useCallback(async () => {
    if (!session?.user?.id || isCalculatingProgress) return;
    
    try {
      setIsCalculatingProgress(true);
      console.log(`Fetching progress for resource: ${id}`);
      
      // First calculate directly from training data for accuracy
      const calculatedProgress = await calculateProgressFromTrainingData();
      
      if (calculatedProgress !== null) {
        console.log(`Using calculated progress: ${calculatedProgress}%`);
        setProgress(calculatedProgress);
        setIsCompleted(calculatedProgress === 100);
        
        // Get the completion date if we've reached 100%
        if (calculatedProgress === 100) {
          await fetchCompletionDate();
        }
        
        // No need to check resource_shares if we already have accurate data
        setIsCalculatingProgress(false);
        return;
      }
      
      // Fallback to resource_shares if calculation fails
      const { data: shareData, error: shareError } = await supabase
        .from('resource_shares')
        .select('progress, completed_at')
        .eq('resource_id', id)
        .eq('trainee_id', session.user.id)
        .maybeSingle();
        
      console.log('Resource shares data:', shareData);
      
      if (!shareError && shareData) {
        // Make sure to use a default value of 0 if progress is null 
        const progressValue = shareData.progress !== null ? shareData.progress : 0;
        console.log(`Using progress from resource_shares: ${progressValue}%`);
        setProgress(progressValue);
        setIsCompleted(progressValue === 100);
        if (shareData.completed_at) {
          setCompletedAt(shareData.completed_at);
        }
      } else {
        setProgress(0);
      }
    } catch (error) {
      console.error('Error in fetchProgress:', error);
      setProgress(0);
    } finally {
      setIsCalculatingProgress(false);
    }
  }, [id, session?.user?.id, isCalculatingProgress]);

  return {
    progress,
    isCompleted,
    completedAt,
    isCalculatingProgress,
    fetchProgress
  };
}
